import 'normalize.css';
import { css } from '@emotion/react';
import { Router, WindowLocation, NavigateFn, globalHistory } from '@reach/router';
import { useCallback, useRef } from 'react';
import { useMount, useUpdate } from 'react-use';
import ReactGA from 'react-ga4';
import ReloadPrompt from './ReloadPrompt'

import
{
	COLOR_EXTRA_LIGHT_GRAY,
	COLOR_FANZTOK_MAIN,
	CONTENT_WIDTH,
	HEADER_HEIGHT,
	MENU_WIDTH,
} from './utility/const';
import
{
	getClientWidth,
	isSmartphone,
} from './utility/utility';
import { Event } from './utility/event';
import { useLocalState } from './hook/use-local-state';
import { useEventSubscribe } from './hook/use-event-subscribe';
import { Page404 } from './component/page-404';
import { Chat } from './component/chat';
import { Verification } from './component/verification';
import logo from './assets/fanza-man-logo-w.svg'
import menuIcon from './assets/menu-icon.svg'
import helpIcon from './assets/help-icon.svg'
import './App.css'

type _RouteComponentProps<TParams = {}> = Partial<TParams> &
{
    path?		: string | undefined;
    default?	: boolean | undefined;
    location?	: WindowLocation | undefined;
    navigate?	: NavigateFn | undefined;
    uri?		: string | undefined;
	onScroll	: any;
	baseRef		: any;
};

const _Page404		= ( props: _RouteComponentProps ) => <Page404 {...props} />;
const _Verification	= ( props: _RouteComponentProps ) => <Verification {...props} />;
const _Chat			= ( props: _RouteComponentProps ) => <Chat {...props} />;

ReactGA.initialize( `G-Q03SCHTRVZ` );

const s = document.createElement( `script` );
s.setAttribute( `src`, `https://platform.twitter.com/widgets.js` );
document.body.appendChild( s );

window.addEventListener( `error`, ( e ) =>
{
	console.error( `[グローバルエラーキャッチ]` )
	console.error( e )
});

window.addEventListener( `unhandledrejection`, ( e ) =>
{
	console.error( `[unhandledrejection]` )
	console.error( e );
	console.error( e.reason );
});

const Style = ( params: any ) => css`
	position				: relative;
	left					: 0px;
	top						: 0px;
	width					: var( --inner-width );
	height					: calc( 100% + env( safe-area-inset-bottom ) );
	background-color		: ${COLOR_EXTRA_LIGHT_GRAY};
	overflow-x				: hidden;
	overflow-y				: hidden;

	--sidePadding			: 12px;
	@media screen and ( min-width : 650px )
	{
		--sidePadding		: 24px;
	}

	.content-base
	{
		width				: 100%;
		height				: 100%;
		max-width			: ${params.contentWidth};
		margin-left			: auto;
		margin-right		: auto;
	}
	.header-base
	{
		position			: fixed;
		top					: 0px;
		width				: 100%;
		max-width			: ${params.contentWidth};
		height				: ${HEADER_HEIGHT}px;
		filter				: drop-shadow( 0px 0px 2px rgba( 0, 0, 0, 0.1 ) );
		transform			: translateZ( 0 );
		background-color	: #FFFFFF;
		z-index				: 100;
		margin-left			: auto;
		margin-right		: auto;
		text-align			: center;

		@keyframes header-hide
		{
			from { top : -${HEADER_HEIGHT}px }
			to   { top : -${HEADER_HEIGHT}px }
		}
		@keyframes header-show
		{
			from { top : 0px; }
			to   { top : 0px; }
		}
		@keyframes header-in
		{
			from { top : -${HEADER_HEIGHT}px; animation-timing-function: ease-out; }
			to   { top : 0px;				  animation-timing-function: ease-out; }
		}
		@keyframes header-out
		{
			from { top : 0px;				  animation-timing-function: ease-out; }
			to   { top : -${HEADER_HEIGHT}px; animation-timing-function: ease-out; }
		}
		top					: -${HEADER_HEIGHT}px;
		animation			: 0.2s ${params.headerMode} forwards;
	}
	.logo-image
	{
		position			: relative;
		height				: calc( ${HEADER_HEIGHT}px - 5px - 5px );
		padding-top			: 5px;
		padding-bottom		: 5px;
	}
	.header-menu-button
	{
		position			: absolute;
		left				: 8px;
		top					: 5px;
		width				: 32px;
		height				: 32px;
		cursor				: pointer;
	}
	.header-menu-button:hover
	{
		transform			: scale( 1.1 );
	}
	.header-help-button
	{
		position			: absolute;
		right				: 10px;
		top					: 5px;
		width				: 32px;
		height				: 32px;
		cursor				: pointer;
		transform			: scale( 0.7 );
	}
	.header-help-button:hover
	{
		transform			: scale( 0.8 );
	}

	.help-window-base
	{
		display				: flex;
		justify-content		: center;
		align-items			: center;
		position			: fixed;
		top					: 0;
		left				: 0;
		bottom				: 0;
		right				: 0;
		z-index				: 1000;
		width				: 100%;
		height				: 100%;
	}
	.help-window-base .help-window
	{
		position			: relative;
		width				: calc( 100vw - 110px );
		height				: calc( 100vh - 110px );
		background-color	: white;
		filter				: drop-shadow( 0px 0px 2px rgba( 0, 0, 0, 0.3 ) );
		padding				: 2em;
		cursor				: pointer;
		border-radius		: 8px;
		overflow-y			: auto;
	}
	.help-window-content
	{
		position			: relative;
		width				: 100%;
		height				: 90%;
		color				: black;
	}
	.detail-footer-item
	{
		display				: inline-block;
	};
	.detail-footer-link
	{
		color				: black;
		text-decoration		: none;
	};
	.detail-footer-link:hover
	{
		color				: ${COLOR_FANZTOK_MAIN};
		text-decoration		: underline;
	};
`;

const App = () =>
{
	const state = useLocalState(
	{
		isResizing		: false,
		innerWidth		: 0,
		innerHeight		: 0,
		headerMode		: `header-show`,
		scrollState		: ``,
		scrollOffset	: 0,
		isFullContent	: false,
		showHelp		: false,
	});
	const update = useUpdate();
	const ref	 = useRef( null );

	const onClickMenu = useCallback( () =>
	{
		Event.publish( `PUSH_MENU_BUTTON` );
	}, [] );

	const onClickHelp = useCallback( () =>
	{
		state.showHelp = true;
		update();
	}, [ state.showHelp, update ] );

	const onClickHideHelp = useCallback( () =>
	{
		state.showHelp = false;
		update();
	}, [ state.showHelp, update ] );

	const onScroll = useCallback( ( params: any ) =>
	{
		if( state.headerMode === `header-out` )
		{
			Event.publish( `HIDE_MENU`, { force: false } );
		}
		state.headerMode = params.scrollDirection === `forward` ? `header-out` : `header-in`;
		update();
	}, [ state.headerMode, update ] );

	const changeWindowSizeEnd = useCallback( () =>
	{
		setTimeout( () =>
		{
			const isEnd = state.innerWidth === window.innerWidth && innerHeight === window.innerHeight;
			if( isEnd )
			{
				state.isResizing = false;
				Event.publish( `CHANGE_WINDOW_SIZE_END`, { width: window.innerWidth, height: window.innerHeight } );
			}
			else
			{
				state.innerWidth  = window.innerWidth;
				state.innerHeight = window.innerHeight;
				changeWindowSizeEnd();
			}
		}, 100 );
	}, [ state.innerWidth, state.innerHeight ] );

	const onResize = useCallback( () =>
	{
		if( state.isResizing === false )
		{
			state.isResizing = true;
			changeWindowSizeEnd();
		}
		document.documentElement.style.setProperty( `--inner-width` , `${window.innerWidth}px`  );
		document.documentElement.style.setProperty( `--inner-height`, `${window.innerHeight}px` );
	}, [ state.isResizing ] );

	const onWindowScroll = useCallback( () =>
	{
	    const scrollOffset = document.documentElement.scrollTop || document.body.scrollTop;
		const scrollDirection = state.scrollOffset - scrollOffset <= 0 ? `forward` : `backward`;
		if( state.scrollState === `fade` )
		{
			if( state.scrollState !== scrollDirection )
			{
				state.scrollState = ``;
			}
		}
		else if( state.scrollState === `` || state.scrollState !== scrollDirection )
		{
			state.scrollState  = scrollDirection;
			state.scrollOffset = scrollOffset;
		}
		else
		{
			if( 40 < Math.abs( state.scrollOffset - scrollOffset ) )
			{
				state.scrollState = `fade`;
				onScroll( { scrollDirection: scrollDirection } );
			}
		}
		state.scrollOffset = scrollOffset;
	}, [ state.scrollState, state.scrollOffset ] );

	const toggleContentWidth = useCallback( ( params: any ) =>
	{
		if( params !== undefined )
		{
			state.isFullContent = params.isFullContent;
		}
		else
		{
			state.isFullContent = !state.isFullContent;
		}
		update();
	}, [ state.isFullContent, update ] );

	const showHeader = useCallback( () =>
	{
		state.headerMode = `header-show`;
		update();
	}, [ state.headerMode ] );

	useEventSubscribe(
	[
		[ `TOGGLE_CONTENT_WIDTH`, toggleContentWidth ],
		[ `SHOW_HEADER`			, showHeader		 ],
	]);

	useMount( () =>
	{
		if( isSmartphone() === false )
		{
			if( CONTENT_WIDTH + MENU_WIDTH * 2 <= getClientWidth() )
			{
				setTimeout( () =>
				{
					Event.publish( `PUSH_MENU_BUTTON` );
				}, 500 );
			}
		}

		onResize();
		window.addEventListener( `scroll`, onWindowScroll );
		window.addEventListener( `resize`, onResize );

		return () =>
		{
			window.removeEventListener( `resize`, onResize );
			window.removeEventListener( `scroll`, onWindowScroll );
		};
	});

	const styleParams =
	{
		headerMode	 : state.headerMode,
		contentWidth : state.isFullContent ? `var( --inner-width )` : `min( ${CONTENT_WIDTH}px, var( --inner-width ) )`,
	};
	return (
		<div id={`app-base`} css={Style( styleParams )}>
			<main id={`content-base`} className={`content-base`} ref={ref}>
				<div className={`header-base`}>
					<img className={`header-menu-button`} src={menuIcon} alt={`menu`} onClick={onClickMenu} />
					<img className={`header-help-button`} src={helpIcon} alt={`menu`} onClick={onClickHelp} />
					<a href={`/`} rel={`noopener noreferrer`}>
						<img src={logo} className={`logo-image`} alt={`logo`} />
					</a>
				</div>
				<Router>
					<_Verification	onScroll={onScroll} baseRef={ref} path={`/`} />
					<_Chat			onScroll={onScroll} baseRef={ref} path={`/chat`} />
					<_Page404		onScroll={onScroll} baseRef={ref} path={`*`} />
				</Router>
				<ReloadPrompt />
			</main>

			{ state.showHelp &&
				<div className={`help-window-base`} onClick={onClickHideHelp}>
					<div className={`help-window`}>
						<div className={`help-window-content`}>
							<h3>最新AIがFANZA動画をオススメするコンセルジュ！</h3>
							ChatGPT API と FANZA API を組み合わせた動画レコメンドサービスです。<br />
							<h3>実装</h3>
							React、vite、TypeScript、Supabase、Firebase を使って作られています。<br />
							<h3>免責事項</h3>
							このサービスは生成AIの実験目的で作られたものです。<br />
							入力された趣味、嗜好に関するいっさいの情報は本サービス側に保存しておりません。また本サービスで紹介した動画等に関する情報の正確性、完全性を保証するものではありません。<br />
							これらの情報によって本サービスの閲覧者に生じたいかなる損害についても、本サービスの開発運営者は一切の責任を負いません。閲覧者の責任においてご利用ください。<br />
							<br />
							<div className={`detail-footer-item`}><a className={`detail-footer-link`} href={`https://twitter.com/fanz_tok`} target={`_blank`} rel={`noopener noreferrer`}>お問い合わせ、ご要望等はこちら</a></div><br />
							<br />
							他にもこんなサービスを作ってます。<br />
							<div className={`detail-footer-item`}><a className={`detail-footer-link`} href={`http://fanz-tok.com`} target={`_blank`} rel={`noopener noreferrer`}>🔞 無限に見られる無料動画！：fanz-tok.com</a></div><br />
							<div className={`detail-footer-item`}><a className={`detail-footer-link`} href={`http://bijyo-tokei.com`} target={`_blank`} rel={`noopener noreferrer`}>⏰ 無限に愛でられる美女時計：美女時計 bijyo-tokei.com</a></div><br />
							<br />
							<br />
						</div>
					</div>
				</div>
			}
		</div>
	);
};

globalHistory.listen( ( { location } ) =>
{
	ReactGA.send( { hitType: `pageview`, page: `${location.pathname}${location.search}` } );

	window.location.reload();	// なぜか必要
});

export default App;
